import React, { useMemo, memo } from 'react';
import PropTypes from 'prop-types';

import Pagination from 'components/Pagination';
import { table as TEXT } from 'locales/en';
import './style.css';

const perPage = [25, 50, 75];

const Table = ({
  titles,
  children,
  pagination,
  className,
  gridClassName,
  onPageChange,
  onClickPerPage,
  hasData,
  topContent,
  emptyCustomView,
}) => {
  const displayPage = useMemo(() => {
    const first = pagination.per_page * (pagination.current_page - 1) + 1;
    const last =
      pagination.total_pages === pagination.current_page
        ? pagination.total
        : pagination.per_page * pagination.current_page;
    return `Showing ${pagination.count ? `${first}-${last}` : '0'} of ${pagination.total ? `${pagination.total}` : 0
      }`;
  }, [pagination]);

  const showPaginationResults = pagination.total > pagination.per_page;
  const showPageNumber = pagination.total_pages > 1;

  return (
    <div className={`${className} p-5`}>
      {topContent}
      {emptyCustomView && !hasData ? (
        emptyCustomView
      ) : (
        <div
          data-testid="grid"
          className={`custom-table grid overflow-x-auto ${gridClassName}`}
        >
          {titles.map((title) => (
            <span
              key={title.name || title}
              className={`text-xs py-3 px-5 mb-2 bg-gray-50 text-javelin-gray-dark font-bold uppercase ${title?.class ? title.class : ''
                }`}
            >
              {title.name || title}
            </span>
          ))}
          {hasData ? (
            children
          ) : (
            <div className="py-24 text-center text-javelin-gray-dark col-span-full">
              <p className="font-bold text-xl mb-5">
                Sorry, there are no results.
              </p>
              <p className="text-sm">
                We suggest changing your filters and try again.
              </p>
            </div>
          )}
        </div>
      )}
      {hasData && pagination.per_page && (
        <div
          className="flex items-center justify-between border-t border-javelin-gray-border"
          data-testid="footer"
        >
          <p className={`text-javelin-gray-dark text-xs md:mr-20 mr-4 ${showPageNumber ? '' : 'mt-4'}`}>
            {displayPage}
          </p>
          {(!pagination.hasStaticPerPage && showPaginationResults) && (
            <div>
              <span className="mr-2 md:mr-5 text-javelin-gray-light text-xs">
                {TEXT.perPage}
              </span>
              {perPage.map((item) => (
                <span
                  key={item}
                  data-testid={`per_page_${item}`}
                  onClick={() => onClickPerPage(item)}
                  className={`cursor-pointer mr-3 text-xs inline-block ${item === pagination.per_page
                    ? 'text-javelin-gray-dark'
                    : 'text-javelin-gray-light'
                    }`}
                >
                  {item}
                </span>
              ))}
            </div>
          )}
          {showPageNumber && (
            <Pagination
              totalPages={pagination.total_pages}
              currentPage={pagination.current_page}
              onPageChange={onPageChange}
            />
          )}
        </div>
      )}
    </div>
  );
};

Table.propTypes = {
  titles: PropTypes.array,
  children: PropTypes.node,
  className: PropTypes.string,
  gridClassName: PropTypes.string,
  pagination: PropTypes.shape({}),
  onPageChange: PropTypes.func,
  onClickPerPage: PropTypes.func,
  hasData: PropTypes.bool,
  topContent: PropTypes.node,
  emptyCustomView: PropTypes.node,
};
Table.defaultProps = {
  titles: ['Id', 'Amount', 'Event'],
  children: <div></div>,
  className: 'card mb-8 overflow-visible',
  gridClassName: '',
  pagination: {},
  onPageChange: () => { },
  onClickPerPage: () => { },
  hasData: true,
  topContent: undefined,
};

export default memo(Table);
